import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DeliveryListRow } from '../../components/dem/DeliveryListRow';
import { PageToolbar } from '../../components/ui/PageToolbar';
import { Modal } from '../../components/ui/Modal';
import {
  fetchDeliveryArrangements,
  fetchLalamoveApiStatus,
  groupDeliveryArrangementBatches,
  syncStaleLalamoveDeliveryStatuses,
  type LalamoveApiStatus,
} from '../../lib/deliveryArrangements';
import { usePortal } from '../../contexts/PortalContext';
import { useDeliveryArrangementsRealtime } from '../../hooks/useDeliveryArrangementsRealtime';
import type { DeliveryArrangement, DeliveryFulfillmentType } from '../../types/delivery';
import {
  formatCrmMonthYear,
  singaporeDateStringFromIso,
  singaporeTodayDateString,
} from '../../utils/dateTime';

const MONTH_VALUES = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'] as const;

type DeliveryBatchHead = {
  head: DeliveryArrangement;
};

type PersonCount = {
  person: string;
  total: number;
};

type MonthVolume = {
  yearMonth: string;
  total: number;
};

function arrangementYearMonth(row: DeliveryArrangement): string {
  const ymd = singaporeDateStringFromIso(row.created_at);
  return ymd.length >= 7 ? ymd.slice(0, 7) : '';
}

function currentDeliveryYearMonth(): string {
  return singaporeTodayDateString().slice(0, 7);
}

function parseYearMonth(yearMonth: string): { year: string; month: string } {
  const m = /^(\d{4})-(\d{2})$/.exec(yearMonth.trim());
  if (m) return { year: m[1], month: m[2] };
  const fallback = currentDeliveryYearMonth();
  return { year: fallback.slice(0, 4), month: fallback.slice(5, 7) };
}

function monthShortLabel(month: string): string {
  const date = new Date(`2020-${month}-15T12:00:00+08:00`);
  return new Intl.DateTimeFormat('en-SG', {
    month: 'short',
    timeZone: 'Asia/Singapore',
  }).format(date);
}

function displayYearMonth(yearMonth: string): string {
  const { year, month } = parseYearMonth(yearMonth);
  return `${monthShortLabel(month)}'${year}`;
}

function personLabel(raw: string | null | undefined): string {
  const trimmed = raw?.trim();
  return trimmed || 'Unspecified';
}

function buildPersonCounts(
  monthBatches: DeliveryBatchHead[],
  pick: (head: DeliveryArrangement) => string | null | undefined,
): PersonCount[] {
  const map = new Map<string, number>();
  for (const batch of monthBatches) {
    const person = personLabel(pick(batch.head));
    map.set(person, (map.get(person) ?? 0) + 1);
  }
  return [...map.entries()]
    .map(([person, total]) => ({ person, total }))
    .sort(
      (a, b) => b.total - a.total || a.person.localeCompare(b.person, undefined, { sensitivity: 'base' }),
    );
}

function DeliveryStatsBarChart({
  title,
  rows,
  tone,
  emptyLabel,
}: {
  title: string;
  rows: PersonCount[];
  tone: 'creator' | 'requested' | 'issue';
  emptyLabel: string;
}) {
  const max = rows[0]?.total || 1;
  const covered = rows.reduce((sum, row) => sum + row.total, 0);
  return (
    <section className={`delivery-stats__section delivery-stats__section--${tone}`}>
      <div className="delivery-stats__section-head">
        <h3 className="delivery-stats__section-title">{title}</h3>
        <p className="delivery-stats__section-meta">
          {rows.length === 0
            ? '0 entries'
            : `${rows.length} ${rows.length === 1 ? 'entry' : 'entries'} · ${covered} total`}
        </p>
      </div>
      {rows.length === 0 ? (
        <p className="delivery-stats__empty">{emptyLabel}</p>
      ) : (
        <div className="delivery-stats__chart" role="img" aria-label={`${title} bar chart`}>
          <div className="delivery-stats__chart-plot" aria-hidden>
            {rows.map((row) => {
              const pct = Math.max(10, Math.round((row.total / max) * 100));
              return (
                <div key={`${title}-${row.person}`} className="delivery-stats__col">
                  <span className="delivery-stats__col-value">{row.total}</span>
                  <div className="delivery-stats__col-track">
                    <div className="delivery-stats__col-fill" style={{ height: `${pct}%` }} />
                  </div>
                  <span className="delivery-stats__col-label" title={row.person}>
                    {row.person}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="delivery-stats__legend" role="list" aria-label={`${title} counts`}>
            {rows.map((row) => (
              <div key={`${title}-legend-${row.person}`} className="delivery-stats__legend-row" role="listitem">
                <span className="delivery-stats__person">{row.person}</span>
                <span className="delivery-stats__num">{row.total}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function DeliveryMonthVolumeChart({
  months,
  selected,
  onSelect,
}: {
  months: MonthVolume[];
  selected: string;
  onSelect: (yearMonth: string) => void;
}) {
  const max = Math.max(1, ...months.map((m) => m.total));
  if (months.length === 0) return null;
  return (
    <section className="delivery-stats__section delivery-stats__section--volume">
      <div className="delivery-stats__section-head">
        <h3 className="delivery-stats__section-title">Monthly volume</h3>
        <p className="delivery-stats__section-meta">Click a month to filter the graphs below</p>
      </div>
      <div className="delivery-stats__volume" role="list" aria-label="Arrangements by month">
        {months.map((month) => {
          const pct = Math.max(8, Math.round((month.total / max) * 100));
          const active = month.yearMonth === selected;
          return (
            <button
              key={month.yearMonth}
              type="button"
              role="listitem"
              className={`delivery-stats__volume-col${active ? ' is-active' : ''}`}
              onClick={() => onSelect(month.yearMonth)}
              aria-pressed={active}
              title={`${formatCrmMonthYear(month.yearMonth)}: ${month.total}`}
            >
              <span className="delivery-stats__volume-value">{month.total}</span>
              <span className="delivery-stats__volume-track" aria-hidden>
                <span className="delivery-stats__volume-fill" style={{ height: `${pct}%` }} />
              </span>
              <span className="delivery-stats__volume-label">{formatCrmMonthYear(month.yearMonth)}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export function DemDeliveriesListPage() {
  const { basePath } = usePortal();
  const [rows, setRows] = useState<DeliveryArrangement[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiStatus, setApiStatus] = useState<LalamoveApiStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | DeliveryFulfillmentType>('all');
  const [statsOpen, setStatsOpen] = useState(false);
  const [yearMonth, setYearMonth] = useState(currentDeliveryYearMonth);
  const { year, month } = parseYearMonth(yearMonth);

  const listBase = basePath ? `${basePath}/deliveries` : '/deliveries';
  const newPath = `${listBase}/new`;

  const setYear = useCallback((nextYear: string) => {
    setYearMonth((prev) => `${nextYear}-${parseYearMonth(prev).month}`);
  }, []);

  const setMonth = useCallback((nextMonth: string) => {
    setYearMonth((prev) => `${parseYearMonth(prev).year}-${nextMonth}`);
  }, []);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [list, status] = await Promise.all([
        fetchDeliveryArrangements(),
        silent ? Promise.resolve(null) : fetchLalamoveApiStatus(),
      ]);
      const synced = await syncStaleLalamoveDeliveryStatuses(list);
      setRows(synced);
      if (status) setApiStatus(status);
    } catch (err) {
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to load deliveries.');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useDeliveryArrangementsRealtime({
    onChange: () => {
      void load({ silent: true });
    },
  });

  const batches = useMemo(() => groupDeliveryArrangementBatches(rows), [rows]);

  const filteredBatches = useMemo(() => {
    let list = batches.filter((batch) => arrangementYearMonth(batch.head) === yearMonth);
    if (typeFilter !== 'all') {
      list = list.filter((batch) => batch.head.fulfillment_type === typeFilter);
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((batch) =>
      batch.stops
        .flatMap((row) => [
          row.client_name,
          row.client_code,
          row.dem_customer_code,
          row.reason,
          row.document_no,
          row.created_by,
          row.requested_by,
          row.issue_caused_by,
          row.status,
          row.fulfillment_type,
        ])
        .map((v) => String(v ?? '').toLowerCase())
        .join('\n')
        .includes(q),
    );
  }, [batches, search, typeFilter, yearMonth]);

  const monthCountByYm = useMemo(() => {
    const map = new Map<string, number>();
    for (const batch of batches) {
      const ym = arrangementYearMonth(batch.head);
      if (!ym) continue;
      map.set(ym, (map.get(ym) ?? 0) + 1);
    }
    return map;
  }, [batches]);

  const yearOptions = useMemo(() => {
    const set = new Set<string>();
    set.add(currentDeliveryYearMonth().slice(0, 4));
    set.add(year);
    for (const ym of monthCountByYm.keys()) set.add(ym.slice(0, 4));
    return [...set].sort((a, b) => b.localeCompare(a));
  }, [monthCountByYm, year]);

  const monthOptions = useMemo(() => {
    const set = new Set<string>();
    set.add(currentDeliveryYearMonth());
    for (const ym of monthCountByYm.keys()) set.add(ym);
    return [...set].sort((a, b) => b.localeCompare(a));
  }, [monthCountByYm]);

  const monthBatches = useMemo(
    () => batches.filter((batch) => arrangementYearMonth(batch.head) === yearMonth),
    [batches, yearMonth],
  );

  const creatorStats = useMemo(
    () => buildPersonCounts(monthBatches, (head) => head.created_by),
    [monthBatches],
  );
  const requestedByStats = useMemo(
    () => buildPersonCounts(monthBatches, (head) => head.requested_by),
    [monthBatches],
  );
  const issueCausedByStats = useMemo(
    () => buildPersonCounts(monthBatches, (head) => head.issue_caused_by),
    [monthBatches],
  );

  const monthVolumes = useMemo<MonthVolume[]>(
    () =>
      [...monthOptions]
        .sort((a, b) => a.localeCompare(b))
        .map((ym) => ({
          yearMonth: ym,
          total: monthCountByYm.get(ym) ?? 0,
        })),
    [monthOptions, monthCountByYm],
  );

  const detailPath = (id: string) => `${listBase}/${id}`;

  const lalamoveStatusText = loading
    ? 'Lalamove API: …'
    : apiStatus?.connected
      ? `Lalamove API: Connected · ${apiStatus.sandbox ? 'Sandbox' : 'Production'}${
          apiStatus.serviceCount > 0 ? ` · ${apiStatus.serviceCount} vehicle types` : ''
        }`
      : `Lalamove API: Unavailable${apiStatus?.error ? ` · ${apiStatus.error}` : ''}`;

  return (
    <div className="delivery-page delivery-page--floating dem-list-scroll">
      <div className="dem-floating-page-actions">
        <p className="dem-po-orders-status dem-po-orders-status--inline" title={lalamoveStatusText}>
          {lalamoveStatusText}
        </p>
        <div className="dem-floating-page-actions__btns">
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={loading}
            onClick={() => void load()}
          >
            Refresh
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={loading}
            onClick={() => setStatsOpen(true)}
          >
            Statistics
          </button>
          <Link to={newPath} className="btn-primary">
            New arrangement
          </Link>
        </div>
      </div>

      <PageToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by account, reason, document number, creator, requested by or issue caused by"
        leading={
          <div className="delivery-page__toolbar-aside delivery-page__toolbar-aside--leading">
            <div className="delivery-page__period">
              <label className="delivery-page__month-field">
                <span>Year</span>
                <select
                  className="field-input delivery-page__year-select"
                  value={year}
                  disabled={loading}
                  onChange={(e) => setYear(e.target.value)}
                  aria-label="Filter by year"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>
              <label className="delivery-page__month-field">
                <span>Month</span>
                <select
                  className="field-input delivery-page__month-select"
                  value={month}
                  disabled={loading}
                  onChange={(e) => setMonth(e.target.value)}
                  aria-label="Filter by month"
                >
                  {MONTH_VALUES.map((mm) => {
                    const count = monthCountByYm.get(`${year}-${mm}`) ?? 0;
                    return (
                      <option key={mm} value={mm}>
                        {monthShortLabel(mm)}
                        {count > 0 ? ` (${count})` : ''}
                      </option>
                    );
                  })}
                </select>
              </label>
            </div>
            <select
              className="field-input delivery-page__type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as 'all' | DeliveryFulfillmentType)}
              aria-label="Filter by type"
            >
              <option value="all">All types</option>
              <option value="lalamove">Lalamove</option>
              <option value="internal">Internal</option>
            </select>
          </div>
        }
      />

      {error ? <div className="alert alert-error">{error}</div> : null}

      {loading && rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__spinner" />
          <p>Loading deliveries…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p>No delivery arrangements yet.</p>
          <Link to={newPath} className="btn-primary btn-sm" style={{ marginTop: '0.75rem' }}>
            New arrangement
          </Link>
        </div>
      ) : filteredBatches.length === 0 ? (
        <div className="empty-state">
          <p>No arrangements match your filters.</p>
        </div>
      ) : (
        <div className="delivery-list card">
          <div className="dem-table-scroll">
            <div className="delivery-list__head" role="row">
              <span>Created</span>
              <span>Type</span>
              <span>Stops</span>
              <span>Fee</span>
              <span>Creator</span>
              <span>Requested by</span>
              <span>Issue caused by</span>
            </div>
            {filteredBatches.map((batch) => (
              <DeliveryListRow
                key={batch.head.id}
                row={batch.head}
                accounts={batch.accounts}
                detailPath={detailPath(batch.head.id)}
              />
            ))}
          </div>
        </div>
      )}

      <Modal
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        title="Delivery statistics"
        wide
        extraWide
        panelClassName="delivery-stats-modal"
      >
        <div className="delivery-stats">
          <p className="delivery-stats__summary">
            Showing <strong>{monthBatches.length}</strong> arrangement
            {monthBatches.length === 1 ? '' : 's'} for{' '}
            <strong>{displayYearMonth(yearMonth)}</strong>
          </p>

          <DeliveryMonthVolumeChart
            months={monthVolumes}
            selected={yearMonth}
            onSelect={setYearMonth}
          />

          {monthBatches.length === 0 ? (
            <p className="delivery-stats__empty">No arrangements for this month.</p>
          ) : (
            <div className="delivery-stats__grid">
              <DeliveryStatsBarChart
                title="Creator"
                rows={creatorStats}
                tone="creator"
                emptyLabel="No creator data for this month."
              />
              <DeliveryStatsBarChart
                title="Requested by"
                rows={requestedByStats}
                tone="requested"
                emptyLabel="No requested-by data for this month."
              />
              <DeliveryStatsBarChart
                title="Issue caused by"
                rows={issueCausedByStats}
                tone="issue"
                emptyLabel="No issue-caused-by data for this month."
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
